import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { DialogOpenEvent } from '@theme/dialog';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Keeps the specs bar and its dialog on the selected variant. A variant change re-renders only the
 * variant picker, so the variant rows, the warranty and the SKU are taken from the section it fetched.
 * On a combined listing the dialog opens on the product on the page and announces each sibling card shown.
 *
 * @extends {Component}
 */
class ProductSpecs extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const container = this.closest('.shopify-section, dialog');
    const { signal } = this.#abortController;
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.querySelector('dialog-component')?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Returns the sibling carousel to the product on the page.
   */
  #handleDialogOpen = () => {
    const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
      this.querySelector('slideshow-component')
    );
    if (!slideshow) return;

    slideshow.select(Number(slideshow.getAttribute('initial-slide')), undefined, { animate: false });
  };

  /**
   * Announces the sibling card a shopper moved to.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated) return;

    const region = this.querySelector('[data-announcement-region]');
    const announcement = event.detail.slide.querySelector('[data-announcement]');
    if (!region || !(announcement instanceof HTMLElement)) return;

    region.textContent = announcement.dataset.announcement ?? '';
  };

  /**
   * Re-renders the specs for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new specs.
        if (!detail?.html || detail.morphElementSelector) return;

        const newSpecs = detail.html.querySelector(`product-specs[data-block-id="${this.dataset.blockId}"]`);
        if (!newSpecs || this.querySelector('dialog[open]')) return;

        morph(this, newSpecs, MORPH_OPTIONS);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-specs] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-specs')) {
  customElements.define('product-specs', ProductSpecs);
}
